/**
 * TouchTooltip — WS-DISC-2 Phase 2 SSOT for the touch-help disclosure.
 *
 * Why this primitive
 * ------------------
 * The legacy contract was a hover-only `Tooltip` whose CSS shipped a
 * `@media (hover: none) { display: none }` kill switch — touch users
 * were silently denied every section help affordance. The remediation
 * has two halves:
 *
 *   1. The hover-capable branch (below) owns the portal-positioned,
 *      hover/focus/touch-tap-pin tooltip surface that the legacy
 *      `Tooltip` used to provide. It is now inlined here so this
 *      primitive is the single tooltip SSOT — no import-time cycle
 *      with `Tooltip.tsx`.
 *   2. The coarse-pointer branch (`TouchTooltipDisclosure`) renders an
 *      aria-expanded disclosure popover — a click-to-open popover,
 *      NOT a tap-pinned transient tooltip — for surfaces that want a
 *      real explainer the user can read at their own pace.
 *
 * Contract
 * --------
 *   <TouchTooltip label="Open the inbox" placement="right">
 *     <button aria-label="Toggle inbox">…</button>
 *   </TouchTooltip>
 *
 *   - On hover-capable pointers (`matchMedia('(hover: hover)').matches`)
 *     TouchTooltip renders the portal-positioned hover/focus tooltip.
 *     Click/tap pins the tooltip; outside-mousedown or Escape unpins.
 *   - On coarse pointers (`matchMedia('(hover: none)').matches`)
 *     TouchTooltip renders an aria-expanded disclosure popover. Click
 *     or tap the trigger to open; click outside or press Escape to
 *     close. The popover is `role="dialog"` with `aria-modal="false"`
 *     because the user can keep interacting with the page (this is a
 *     popover, not a modal).
 *   - Escape closes via `useKeyboardScope` — the keyboard SSOT (see
 *     `core/keyboard/`). A bare `window.addEventListener('keydown')`
 *     would be a WS-GOV-9 rule 8 violation.
 *
 * A11y
 * ----
 *   - Hover branch trigger carries `aria-describedby` while the
 *     tooltip is visible; tooltip node is `role="tooltip"`.
 *   - Disclosure branch trigger carries `aria-controls={popoverId}`
 *     + `aria-expanded`; popover is `role="dialog"` `aria-modal="false"`
 *     `aria-label`d.
 *   - Click-outside dismissal on both branches is wired via a document
 *     mousedown listener gated on the open/pinned state, mirroring
 *     `SectionHelpButton`.
 *
 * SSR / test safety
 * -----------------
 *   - When `window` is undefined we fall back to the hover-capable
 *     branch. SSR has no notion of pointer type.
 *   - When matchMedia is missing (older test runners) the resolver
 *     defaults to `hover-capable` — the LEAST surprising default for
 *     a desktop-first product.
 */

import {
    cloneElement,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type FocusEvent,
    type KeyboardEvent as ReactKeyboardEvent,
    type MouseEvent,
    type PointerEvent as ReactPointerEvent,
    type ReactElement,
    type RefCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { useKeyboardScope } from '../keyboard';
import type { KeyComboShortcut } from '../keyboard';
import { captureError } from '../errors/captureError';
// Tooltip.css used to ship the `.ui-tooltip` family separately. Since
// the legacy Tooltip primitive was deleted (see the no-bare-tooltip
// rule header), those rules were inlined into TouchTooltip.css — this
// file is now the sole CSS module for the tooltip primitive.
import './TouchTooltip.css';

type Placement = 'top' | 'right' | 'bottom' | 'left';

type TouchTooltipProps = {
    /** The disclosure copy. Required — the popover has no purpose
     *  without it. Kept as a flat string so the primitive can drive
     *  both an `aria-label` on the dialog and the visible body text
     *  without forcing callers to thread a separate accessible name. */
    label: string;
    /** Anchor side relative to the trigger. Default `right`. Mirrors
     *  the legacy `Tooltip` prop shape so callers can swap freely. */
    placement?: Placement;
    /** Pixel gap between trigger edge and tooltip (hover branch). The
     *  coarse-pointer disclosure path anchors inline so the offset is
     *  currently a no-op on touch. Default 10. */
    offset?: number;
    /** Delay in ms before the hover tooltip appears. Default 300 —
     *  matches the legacy Tooltip's snappy-but-not-twitchy default.
     *  Set to 0 for immediate. */
    delayMs?: number;
    /** Single interactive child. The component clones it to attach
     *  event + ref + aria wiring. */
    children: ReactElement;
};

type Position = { top: number; left: number } | null;

function computePosition(
    rect: DOMRect,
    placement: Placement,
    offset: number,
): { top: number; left: number } {
    // No `default` branch: the non-nullable return type forces TS to flag
    // any new Placement variant that fails to add a case here. Matches the
    // `_exhaustive: never` pattern used elsewhere (see NotificationsBell).
    switch (placement) {
        case 'right':
            return { top: rect.top + rect.height / 2, left: rect.right + offset };
        case 'left':
            return { top: rect.top + rect.height / 2, left: rect.left - offset };
        case 'top':
            return { top: rect.top - offset, left: rect.left + rect.width / 2 };
        case 'bottom':
            return { top: rect.bottom + offset, left: rect.left + rect.width / 2 };
    }
}

/** Resolve the pointer capability ONCE at first render. The result
 *  drives the branch choice for the lifetime of the component instance.
 *  Re-evaluating on every render would create flicker if the device
 *  ever toggles (e.g. tablet + keyboard) — that's an explicit
 *  non-goal of Phase 2; the strangler swap targets the common case. */
function detectHoverCapable(): boolean {
    if (typeof window === 'undefined') return true;
    if (typeof window.matchMedia !== 'function') return true;
    try {
        return window.matchMedia('(hover: hover)').matches;
    } catch (err) {
        captureError(err, { scope: 'TouchTooltip', op: 'matchMedia' });
        return true;
    }
}

/** Mounts only while the hover-branch tooltip is pinned (touch tap /
 *  keyboard Enter-Space pin). Owns the Escape-to-unpin shortcut via
 *  the keyboard SSOT so the dispatcher sees a clean stack of pinned
 *  tooltips and any surrounding dialogs. */
function TooltipPinnedKeyScope({
    shortcuts,
}: {
    shortcuts: ReadonlyArray<KeyComboShortcut>;
}) {
    useKeyboardScope(shortcuts, { owner: 'TouchTooltip' });
    return null;
}

/** Hover-capable branch — the portal-positioned hover/focus tooltip
 *  with touch-tap pin. Inlined from the legacy `Tooltip` primitive so
 *  this file is now the sole tooltip SSOT. */
function TouchTooltipHover({
    label,
    placement,
    offset,
    delayMs,
    children,
}: {
    label: string;
    placement: Placement;
    offset: number;
    delayMs: number;
    children: ReactElement;
}) {
    const [pos, setPos] = useState<Position>(null);
    // `pinned` survives mouseleave/blur — used by the touch-tap path
    // and by keyboard Enter/Space. Pin and hover are independent
    // visibility sources: either being true keeps the tooltip up.
    const [pinned, setPinned] = useState(false);
    const tooltipId = useId();
    const triggerRef = useRef<HTMLElement | null>(null);
    const showTimerRef = useRef<number | null>(null);

    const cancelPending = useCallback(() => {
        if (showTimerRef.current !== null) {
            window.clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
        }
    }, []);

    const measure = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        setPos(computePosition(rect, placement, offset));
    }, [placement, offset]);

    const show = useCallback(() => {
        cancelPending();
        if (!triggerRef.current) return;
        if (delayMs <= 0) measure();
        else showTimerRef.current = window.setTimeout(measure, delayMs);
    }, [cancelPending, delayMs, measure]);

    const hide = useCallback(() => {
        cancelPending();
        // When pinned, hover/blur must not clear the tooltip — pin is
        // the authoritative visibility source on touch + keyboard.
        if (pinned) return;
        setPos(null);
    }, [cancelPending, pinned]);

    // Touch tap / explicit click toggles the pinned state. We measure
    // immediately (delayMs is bypassed) so the tap feels responsive.
    const togglePinned = useCallback(() => {
        setPinned((prev) => {
            const next = !prev;
            if (next) {
                cancelPending();
                measure();
            } else {
                setPos(null);
            }
            return next;
        });
    }, [cancelPending, measure]);

    // Hide on scroll/resize — the fixed-position tooltip would otherwise
    // float in a stale location as the page moves under it. We treat
    // scroll/resize as a forced dismiss (clears pin too) because the
    // anchor has moved off the user's tap point.
    useEffect(() => {
        if (!pos) return;
        const onInvalidate = () => {
            cancelPending();
            setPinned(false);
            setPos(null);
        };
        window.addEventListener('scroll', onInvalidate, true);
        window.addEventListener('resize', onInvalidate);
        return () => {
            window.removeEventListener('scroll', onInvalidate, true);
            window.removeEventListener('resize', onInvalidate);
        };
    }, [pos, cancelPending]);

    // Outside-tap unpins. Mirrors the SectionHelpButton pattern
    // (59f5c41): document mousedown listener gated on pinned. Escape
    // is owned by `TooltipPinnedKeyScope` below — routed through the
    // keyboard SSOT so the dispatcher composes with surrounding modals.
    useEffect(() => {
        if (!pinned) return;
        const onPointerDown = (e: globalThis.MouseEvent) => {
            const target = e.target as Node | null;
            if (!target) return;
            if (triggerRef.current?.contains(target)) return;
            setPinned(false);
            setPos(null);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [pinned]);

    const escShortcuts = useMemo<ReadonlyArray<KeyComboShortcut>>(
        () => [
            {
                combo: 'escape',
                handler: () => {
                    setPinned(false);
                    setPos(null);
                },
            },
        ],
        [],
    );

    useEffect(() => cancelPending, [cancelPending]);

    // Merge our measurement ref with any ref the child already carries.
    // React 19: `ref` is a regular prop — `children.ref` is gone.
    // Object refs are rare on tooltip targets and mutating them from
    // here is brittle, so we silently skip that case; consumers that
    // need both an object ref AND a tooltip can attach the tooltip
    // externally.
    const childRef = (children.props as { ref?: unknown }).ref;
    const mergedRef: RefCallback<HTMLElement> = useCallback((node) => {
        triggerRef.current = node;
        if (typeof childRef === 'function') childRef(node);
    }, [childRef]);

    type ChildHandlers = {
        onMouseEnter?: (e: MouseEvent<HTMLElement>) => void;
        onMouseLeave?: (e: MouseEvent<HTMLElement>) => void;
        onFocus?: (e: FocusEvent<HTMLElement>) => void;
        onBlur?: (e: FocusEvent<HTMLElement>) => void;
        onClick?: (e: MouseEvent<HTMLElement>) => void;
        onPointerDown?: (e: ReactPointerEvent<HTMLElement>) => void;
        onKeyDown?: (e: ReactKeyboardEvent<HTMLElement>) => void;
    };
    const originalProps = children.props as ChildHandlers;

    // `visible` collapses the two independent visibility sources
    // (hover-driven `pos` + tap-driven `pinned`) into a single
    // aria-describedby signal. Without this, screen readers would
    // miss the pinned state when the tooltip was pinned-but-not-hovered.
    const visible = pos !== null;

    // Why: ref-callback through cloneElement is opaque to react-hooks/refs.
    // `mergedRef` writes triggerRef.current at commit time (after render);
    // the lint rule can't follow the callback through cloneElement so it
    // conservatively flags this line. Pattern is correct.
    // see docs/typography.md for the SSOT Tooltip primitive contract.
    // eslint-disable-next-line react-hooks/refs
    const trigger = cloneElement(children as ReactElement<Record<string, unknown>>, {
        ref: mergedRef,
        'aria-describedby': visible ? tooltipId : undefined,
        onMouseEnter: (e: MouseEvent<HTMLElement>) => {
            originalProps.onMouseEnter?.(e);
            show();
        },
        onMouseLeave: (e: MouseEvent<HTMLElement>) => {
            originalProps.onMouseLeave?.(e);
            hide();
        },
        onFocus: (e: FocusEvent<HTMLElement>) => {
            originalProps.onFocus?.(e);
            show();
        },
        onBlur: (e: FocusEvent<HTMLElement>) => {
            originalProps.onBlur?.(e);
            hide();
        },
        onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
            originalProps.onPointerDown?.(e);
            // Only the touch path uses pointerdown to toggle. Mouse and
            // pen rely on hover/focus + the click handler below — using
            // pointerdown for mouse would fight click-through on
            // buttons whose primary action is the click itself.
            if (e.pointerType === 'touch') togglePinned();
        },
        onClick: (e: MouseEvent<HTMLElement>) => {
            originalProps.onClick?.(e);
            // Click toggles pin for both mouse and keyboard (Enter/Space
            // synthesise click on native buttons). jsdom emits click
            // without preceding pointerdown so this is also the path
            // exercised by the test suite.
            togglePinned();
        },
        onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => {
            originalProps.onKeyDown?.(e);
            // Escape from a focused trigger unpins immediately, even
            // before the document-level listener runs (focused element
            // event ordering varies across browsers).
            if (e.key === 'Escape' && pinned) {
                setPinned(false);
                setPos(null);
            }
        },
    });

    return (
        <>
            {trigger}
            {pinned && <TooltipPinnedKeyScope shortcuts={escShortcuts} />}
            {visible && typeof document !== 'undefined'
                ? createPortal(
                    <div
                        id={tooltipId}
                        role="tooltip"
                        className={`ui-tooltip ui-tooltip--${placement}`}
                        style={{ top: pos.top, left: pos.left }}
                    >
                        {label}
                    </div>,
                    document.body,
                )
                : null}
        </>
    );
}

/** Mounts only while the coarse-pointer disclosure is open. Owns the
 *  Escape-to-close shortcut via the keyboard SSOT — composes with
 *  surrounding modals + popovers rather than racing them via a bare
 *  window listener. */
function TouchTooltipKeyScope({ onEscape }: { onEscape: () => void }) {
    const shortcuts = useMemo<ReadonlyArray<KeyComboShortcut>>(
        () => [{ combo: 'escape', handler: () => onEscape() }],
        [onEscape],
    );
    useKeyboardScope(shortcuts, { owner: 'TouchTooltip' });
    return null;
}

/** Coarse-pointer branch — the aria-expanded disclosure popover.
 *  Lives in a separate component so the hover-capable branch can stay
 *  lean without paying for any of the disclosure state (refs,
 *  listeners, key scope). */
function TouchTooltipDisclosure({
    label,
    children,
}: {
    label: string;
    children: ReactElement;
}) {
    const popoverId = useId();
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);

    // Outside-tap dismissal. The listener is only installed while
    // `open` so the cost is zero in the steady state. Mirrors the
    // SectionHelpButton pattern (59f5c41).
    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: globalThis.MouseEvent) => {
            const target = e.target as Node | null;
            if (!target) return;
            if (triggerRef.current?.contains(target)) return;
            if (popoverRef.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [open]);

    const handleEscape = useCallback(() => {
        setOpen(false);
        // Refocus the trigger so keyboard users keep their place in the
        // tab order — same recovery affordance as SectionHelpButton.
        const el = triggerRef.current as HTMLElement | null;
        if (el && typeof el.focus === 'function') el.focus();
    }, []);

    // Merge our ref with any ref the child already carries. Object
    // refs on tooltip targets are rare; we silently skip that case to
    // avoid brittle mutation (same rationale as the hover branch above).
    const childRef = (children.props as { ref?: unknown }).ref;
    const mergedRef: RefCallback<HTMLElement> = useCallback((node) => {
        triggerRef.current = node;
        if (typeof childRef === 'function') childRef(node);
    }, [childRef]);

    type ChildHandlers = {
        onClick?: (e: MouseEvent<HTMLElement>) => void;
        onKeyDown?: (e: ReactKeyboardEvent<HTMLElement>) => void;
        onFocus?: (e: FocusEvent<HTMLElement>) => void;
    };
    const originalProps = children.props as ChildHandlers;

    // Why: ref-callback through cloneElement is opaque to react-hooks/refs.
    // `mergedRef` writes triggerRef.current at commit time; the lint rule
    // can't follow the callback through cloneElement so it conservatively
    // flags this line. Pattern matches the hover branch above.
    // eslint-disable-next-line react-hooks/refs
    const trigger = cloneElement(children as ReactElement<Record<string, unknown>>, {
        ref: mergedRef,
        'aria-controls': popoverId,
        'aria-expanded': open,
        onClick: (e: MouseEvent<HTMLElement>) => {
            originalProps.onClick?.(e);
            setOpen((v) => !v);
        },
        onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => {
            originalProps.onKeyDown?.(e);
            // jsdom does not synthesise click on Enter/Space for non-
            // button elements; handling explicitly keeps the contract
            // deterministic regardless of the child's tag.
            if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                setOpen((v) => !v);
            }
        },
    });

    return (
        <span className="touch-tooltip-wrap">
            {trigger}
            {open && (
                <div
                    ref={popoverRef}
                    id={popoverId}
                    role="dialog"
                    aria-modal="false"
                    aria-label={label}
                    className="touch-tooltip-popover"
                >
                    <TouchTooltipKeyScope onEscape={handleEscape} />
                    <p className="touch-tooltip-popover__label">{label}</p>
                </div>
            )}
        </span>
    );
}

/** Public entry point. Branches once on pointer capability and stays
 *  on that branch for the component instance's lifetime. */
export default function TouchTooltip({
    label,
    placement = 'right',
    offset = 10,
    delayMs = 300,
    children,
}: TouchTooltipProps) {
    // Memoise so the branch decision is stable across re-renders.
    // Re-deciding per render would force every legacy Tooltip consumer
    // to reset its hover timer on every parent re-render.
    const hoverCapable = useMemo(() => detectHoverCapable(), []);

    if (hoverCapable) {
        return (
            <TouchTooltipHover
                label={label}
                placement={placement}
                offset={offset}
                delayMs={delayMs}
            >
                {children}
            </TouchTooltipHover>
        );
    }

    return <TouchTooltipDisclosure label={label}>{children}</TouchTooltipDisclosure>;
}

export type { TouchTooltipProps };
